import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/client';
import { campaignsApi, contactsApi, type Campaign } from '../../api/campaigns';
import {
  Alert,
  Button,
  Card,
  CardGrid,
  ConfirmDialog,
  Metric,
  Section,
  StatusChip,
} from '../../components';
import { useI18n, type TranslationKey } from '../../i18n';
import { useSession } from '../../auth/session-context';
import { formatDate } from '../../format';

/** Kurze Übersicht und die wenigen Aktionen, die unmittelbar gebraucht werden. */
export function OverviewTab({ campaign, onChange }: { campaign: Campaign; onChange: () => void }): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();

  const isAdmin = principal?.kind === 'tenant_user' && principal.isAdmin;

  const [contacts, setContacts] = useState<number | null>(null);
  const [callers, setCallers] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<'archivieren' | 'loeschen' | null>(null);

  const load = useCallback(async () => {
    try {
      const list = await contactsApi.list(campaign.id, { limit: 1 });
      setContacts(list.total);
      if (isAdmin) {
        const members = await campaignsApi.listMembers(campaign.id);
        setCallers(members.members.length);
      }
    } catch (caught) {
      handleApiError(caught);
    }
  }, [campaign.id, handleApiError, isAdmin]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: () => Promise<unknown>): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      await action();
      onChange();
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setBusy(false);
    }
  };

  const stateKey: TranslationKey = campaign.archivedAt
    ? 'campaigns.state.archived'
    : campaign.isActive
      ? 'campaigns.state.active'
      : 'campaigns.state.stopped';

  return (
    <div className="steps">
      {error ? <Alert tone="error">{error}</Alert> : null}

      <CardGrid>
        <Metric label={t('campaign.overview.contacts')} value={contacts === null ? '–' : String(contacts)} />
        {isAdmin ? (
          <Metric label={t('campaign.overview.callers')} value={callers === null ? '–' : String(callers)} />
        ) : null}
        <Metric label={t('campaign.overview.mode')} value={t(`campaigns.mode.${campaign.campaignMode}`)} />
        <Metric label={t('campaign.overview.created')} value={formatDate(campaign.createdAt)} />
      </CardGrid>

      <Card>
        <Section
          title={t('campaign.overview.state')}
          description={campaign.description ?? undefined}
          actions={
            <StatusChip tone={campaign.archivedAt ? 'neutral' : campaign.isActive ? 'erfolg' : 'warnung'}>
              {t(stateKey)}
            </StatusChip>
          }
        >
          {isAdmin ? (
            <div className="row-actions">
              {campaign.archivedAt ? (
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() => void run(() => campaignsApi.restore(campaign.id))}
                >
                  {t('campaign.restore')}
                </Button>
              ) : (
                <>
                  {/* Starten und Stoppen sind alltäglich und brauchen keine Rückfrage. */}
                  <Button
                    variant={campaign.isActive ? 'secondary' : 'primary'}
                    disabled={busy}
                    onClick={() => void run(() => campaignsApi.setActive(campaign.id, !campaign.isActive))}
                  >
                    {campaign.isActive ? t('campaign.stop') : t('campaign.start')}
                  </Button>
                  <Button
                    disabled={busy || campaign.isActive}
                    onClick={() => setDialog('archivieren')}
                  >
                    {t('campaign.archive')}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy || campaign.isActive}
                    onClick={() => setDialog('loeschen')}
                  >
                    {t('campaign.delete')}
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </Section>
      </Card>

      {dialog === 'archivieren' ? (
        <ConfirmDialog
          title={t('dialog.campaignArchive')}
          message={t('dialog.campaignArchiveText')}
          confirmLabel={t('campaign.archive')}
          tone="primary"
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            setDialog(null);
            void run(() => campaignsApi.archive(campaign.id));
          }}
        />
      ) : null}

      {dialog === 'loeschen' ? (
        <ConfirmDialog
          title={t('dialog.campaignDelete')}
          message={t('dialog.campaignDeleteText')}
          confirmLabel={t('campaign.delete')}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            setDialog(null);
            void run(() => campaignsApi.remove(campaign.id));
          }}
        />
      ) : null}
    </div>
  );
}
